import axios from 'axios';
import { API_URL } from '../config/constants';
import SpringDataPage from '../@types/spring-data-page';
import { getErrorMessage } from '../utils/utils';
import { JobExecutionContext as Job, JobResult, JobExecutionContext } from '../@types/job';


/**
 * Job management service APIs
 */
class JobManagementService {
    // API URL definitions
    static jobsUrlV1 = API_URL + 'jobs';
    static jobsUrlV2 = API_URL + 'api/v2/jobs';

    async fetchPagedJobs(
        page: number = 0,
        size: number = 20,
        filters?: {
            jobType?: string;
            submittedBy?: string;
            correlationId?: string;
            states?: Array<string>;
            onlyFailed?: boolean;
        }
    ): Promise<SpringDataPage<Job>> {
        try {
            // Build query parameters
            const params = new URLSearchParams({
                page: page.toString(),
                size: size.toString(),
            });
            if (filters?.jobType) {
                params.append('jobType', filters.jobType);
            }
            if (filters?.submittedBy) {
                params.append('submittedBy', filters.submittedBy);
            }
            if (filters?.correlationId) {
                params.append('correlationId', filters.correlationId);
            }
            if (filters?.states) {
                params.append('states', JSON.stringify(filters.states));
            }
            if (filters?.onlyFailed) {
                params.append('onlyFailed', filters.onlyFailed.toString());
            }
            const url = `${JobManagementService.jobsUrlV2}?${params.toString()}`;
            const response = await axios.get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            });
            return response.data;
        } catch (error) {
            throw new Error(`Failed to fetch job list: ${getErrorMessage(error)}`);
        }
    }


    /**
     * Get job details by ID
     */
    async getJobDetails(jobId: number): Promise<Job> {
        try {
            const url = `${JobManagementService.jobsUrlV2}/${jobId}`;
            const response = await axios.get<Job>(url, {
                withCredentials: true,
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            throw new Error(`Failed to fetch job details: ${getErrorMessage(error)}`);
        }
    }

    /**
     * Get job result
     */
    async getJobResult(jobId: number): Promise<JobResult> {
        try {
            const url = `${JobManagementService.jobsUrlV2}/${jobId}/result`;
            const response = await axios.get(url, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            throw new Error(`Failed to fetch job result: ${getErrorMessage(error)}`);
        }
    }

    /**
     * Cancel a running Job
     */
    async cancelJob(jobId: number): Promise<void> {
        try {
            const url = `${JobManagementService.jobsUrlV2}/${jobId}`;
            await axios.delete(url, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'                }
            });
            
        } catch (error) {
            throw new Error(`Failed to cancel job: ${getErrorMessage(error)}`);
        }
    }

    /**
     * Get active jobs
     */
    async getActiveJobs(): Promise<Array<JobExecutionContext>> {
        try {
            const url = `${JobManagementService.jobsUrlV2}/active`;
            const response = await axios.get<Array<JobExecutionContext>>(url, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            throw new Error(`Failed to fetch active jobs: ${getErrorMessage(error)}`);
        }
    }

    /**
     * Find job by correlation ID
     */
    async findJobByCorrelationId(correlationId: number): Promise<JobExecutionContext> {
        try {
            const url = `${JobManagementService.jobsUrlV2}/correlation/${correlationId}`;
            const response = await axios.get<JobExecutionContext>(url, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            throw new Error(`Failed to find job by correlation ID: ${getErrorMessage(error)}`);
        }
    }

}

export default new JobManagementService();